import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, AreaChart, Area, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ArrowLeft, BarChart3, Plane } from "lucide-react";
import { api } from "@/lib/api";
import TravelPlan from "@/components/TravelPlan";
import { useStatusFilter } from "@/context/StatusFilterContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendBadge } from "@/components/TrendBadge";

const C = { bize: "#06B6D4", karsi: "#EF4444", field: "#10B981", work: "#F59E0B" };
const axis = { fontSize: 11, fill: "#94a3b8" };
const grid = "#1e293b";
const tip = { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 };

function ChartCard({ title, children, badge }) {
  return <Card className="p-4 border border-border"><div className="flex items-start justify-between gap-2 mb-2"><h3 className="font-display font-semibold text-sm">{title}</h3>{badge && <div className="flex items-center gap-1 shrink-0">{badge}</div>}</div><div className="h-60 w-full">{children}</div></Card>;
}

export default function CommitteeDetail() {
  const { cno } = useParams();
  const { status } = useStatusFilter();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("analiz");
  const ref = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/committee/${cno}?status=${status}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [cno, status]);

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-56 rounded-lg" />)}</div>;
  if (!d) return <p className="text-muted-foreground">Komite verisi yüklenemedi.</p>;
  const s = d.summary, weeks = d.weeks || [];
  const cards = [
    { l: "Kayıtlı", v: s.registered }, { l: "Ulaşılan", v: s.reached },
    { l: "Bizim Oy", v: s.bize, c: C.bize }, { l: "Karşı Oy", v: s.karsi, c: C.karsi },
    { l: "Çalışma Skoru", v: `%${s.work_score}`, c: C.work }, { l: "Saha %", v: `%${s.field_pct}`, c: C.field },
    { l: "Kazanma Hedefi", v: s.target ?? "-", c: "#0F2C59" },
    { l: "Yetki Belgesi", v: `${s.belge}/${s.belge_target}`, c: "#7C3AED" },
  ];

  return (
    <div className="space-y-4" data-testid="committee-detail-page" ref={ref}>
      <div>
        <button onClick={() => navigate("/panel")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="committee-back"><ArrowLeft className="h-3.5 w-3.5" /> Genel Panel</button>
        <h1 className="font-display text-2xl font-bold">#{d.committee_no} {d.name}</h1>
        <p className="text-sm text-muted-foreground">Hedef {s.target} firma{s.field_turnout_pct ? ` (beklenen katılım %${s.field_turnout_pct} → yarısı+1)` : ""} · Filtre: {status === "faal" ? "Faal" : status === "aski" ? "Askı" : "Tümü"}</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2" data-testid="cd-tabs">
        {[["analiz", "Analizler", BarChart3], ["seyahat", "Seyahat Planı", Plane]].map(([k, lb, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`cd-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
              tab === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
            <Icon className="h-4 w-4" /> {lb}
          </button>
        ))}
      </div>

      {tab === "seyahat" && (
        <TravelPlan committeeNo={d.committee_no} lockCommittee
          title={`Seyahat Planı — K${d.committee_no} ${d.name}`} />
      )}

      {tab === "analiz" && (<>
      <div className="grid grid-cols-2 lg:grid-cols-8 gap-3">
        {cards.map((k) => (
          <Card key={k.l} className="p-3 border border-border" data-testid={`cd-card-${k.l}`}>
            <p className="text-xs text-muted-foreground">{k.l}</p>
            <p className="text-xl font-bold mt-0.5" style={{ color: k.c || undefined }}>{k.v}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Bizim vs Karşı Oy — Günlük Seyir"
          badge={<><TrendBadge data={weeks} dataKey="bize" label="Bizim" /><TrendBadge data={weeks} dataKey="karsi" label="Karşı" goodWhenUp={false} /><TrendBadge data={weeks} dataKey="belge" label="Belge" /></>}>
          <ResponsiveContainer><LineChart data={weeks}>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="bize" name="Bizim" stroke={C.bize} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} /><Line type="monotone" dataKey="karsi" name="Karşı" stroke={C.karsi} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} /><Line type="monotone" dataKey="belge" name="Yetki Belgesi" stroke="#7C3AED" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
          </LineChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Kümülatif Oy (Alan)" badge={<TrendBadge data={weeks} dataKey="bize" label="Bizim" />}>
          <ResponsiveContainer><AreaChart data={weeks}>
            <defs><linearGradient id="cb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.bize} stopOpacity={0.6} /><stop offset="100%" stopColor={C.bize} stopOpacity={0.05} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="bize" name="Bizim" stroke={C.bize} fill="url(#cb)" /><Area type="monotone" dataKey="karsi" name="Karşı" stroke={C.karsi} fillOpacity={0.1} fill={C.karsi} />
          </AreaChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Saha Çalışması + Çalışma Skoru" badge={<TrendBadge data={weeks} dataKey="work_score" label="Skor" />}>
          <ResponsiveContainer><ComposedChart data={weeks}>
            <defs><linearGradient id="cf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.field} stopOpacity={0.5} /><stop offset="100%" stopColor={C.field} stopOpacity={0.05} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis yAxisId="l" tick={axis} /><YAxis yAxisId="r" orientation="right" tick={axis} /><Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Area yAxisId="l" type="monotone" dataKey="field" name="Saha Teması" stroke={C.field} fill="url(#cf)" /><Line yAxisId="r" type="monotone" dataKey="work_score" name="Çalışma Skoru %" stroke={C.work} strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart></ResponsiveContainer>
        </ChartCard>
        <Card className="p-4 border border-border">
          <h3 className="font-display font-semibold text-sm mb-2">Komite Üyeleri (Sorumlu Bazlı)</h3>
          <div className="max-h-60 overflow-auto space-y-1.5">
            {d.members.map((m, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-border/50 pb-1.5" data-testid={`cd-member-${i}`}>
                <span className="truncate">{m.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">{m.assigned} firma · {m.kesin} kesin · {m.reached} ulaşılan</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      </>)}
    </div>
  );
}
